import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FilterSwitch, IFilterSwitchOption } from 'components/FilterSwitch/FilterSwitch';
import { CardToggle, ICardToggleOption } from 'components/Utils/CardToggle';
import { supportedUILanguages } from 'globalConstants';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapLargePlus5,
  globalStackTokensGapMedium,
  globalTextStylesBold,
} from 'globalStyles';
import Language from 'models/language';
import { TenantOnboarding, TrialWelcomeIntent } from 'models/tenant';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ITrialWelcomeLanguageProps {
  onboardingState: TenantOnboarding;
  updateOrgLang: (langCode: string) => Promise<void>;
  updateUserLang: (langCode: string) => Promise<void>;
  languages: Language[];
  selectedLang: Language | undefined;
  onNext: (state: TenantOnboarding) => void;
}

const TrialWelcomeLanguage = (props: ITrialWelcomeLanguageProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'adminCompany']);
  const [selectedKey, setSelectedKey] = useState<TrialWelcomeIntent | undefined>(
    props.onboardingState.onboardingSteps.intent,
  );

  //one chip per supported UI language, keyed on the language code that sets the language in the backend and frontend
  const languageOptions: IFilterSwitchOption<string>[] = Array.from(supportedUILanguages.entries()).map(
    ([code, name]) => ({ key: code, label: name }),
  );
  //no language chosen yet reads as the first one, the language the tenant starts on
  const selectedLanguageCode = props.selectedLang?.code ?? languageOptions[0].key;

  const applyNewState = async (state: TenantOnboarding): Promise<TenantOnboarding> => {
    if (!props.selectedLang) return state;
    const newOnboardingState = state.clone();
    newOnboardingState.onboardingSteps.languageIsSet = true;
    newOnboardingState.onboardingSteps.intent = selectedKey;
    await props.updateOrgLang(props.selectedLang.code);

    return newOnboardingState;
  };

  const getOptions = (): ICardToggleOption[] => {
    return [
      {
        key: TrialWelcomeIntent.IsFirstISO,
        iconName: 'FavoriteStar',
        subText: t('dashboard:TrialWelcome.Situation.Options.Option1.Title'),
        text: t('dashboard:TrialWelcome.Situation.Options.Option1.Text'),
      },
      {
        key: TrialWelcomeIntent.IsCertified,
        iconName: 'AddTo',
        subText: t('dashboard:TrialWelcome.Situation.Options.Option2.Title'),
        text: t('dashboard:TrialWelcome.Situation.Options.Option2.Text'),
      },
      {
        key: TrialWelcomeIntent.IsConsultant,
        iconName: 'UserFollowed',
        subText: t('dashboard:TrialWelcome.Situation.Options.Option3.Title'),
        text: t('dashboard:TrialWelcome.Situation.Options.Option3.Text'),
      },
    ];
  };

  //
  // Main render
  //
  return (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapMedium}
      horizontalAlign="center"
      styles={{ root: { width: 900, paddingRight: 20, paddingBottom: 20 } }}
    >
      <IPStack.Item>
        <IPStack verticalFill tokens={globalStackTokensGapLargePlus5}>
          <IPStack tokens={globalStackTokensGapExtraSmall}>
            <IPText block variant="large" styles={globalTextStylesBold}>
              {t('dashboard:TrialWelcome.Language.Title')}
            </IPText>
            <IPText block>{t('dashboard:TrialWelcome.Language.SubTitle')}</IPText>
          </IPStack>
          <IPStack.Item>
            <FilterSwitch
              options={languageOptions}
              selectedKey={selectedLanguageCode}
              onChange={(code) => void props.updateUserLang(code)}
            />
          </IPStack.Item>
          <IPStack tokens={globalStackTokensGapExtraSmall}>
            <IPText block variant="large" styles={globalTextStylesBold}>
              {t('dashboard:TrialWelcome.Situation.Title')}
            </IPText>
            <IPText block>{t('dashboard:TrialWelcome.Situation.SubTitle')}</IPText>
          </IPStack>
          <IPStack.Item>
            <CardToggle
              options={getOptions()}
              selectedKeys={selectedKey ? [selectedKey] : undefined}
              onSelect={(keys) => setSelectedKey(keys[0] as number)}
            />
          </IPStack.Item>
          <IPStack.Item>
            <IPPrimaryButton
              disabled={!props.selectedLang || !selectedKey}
              text={t('dashboard:TrialWelcome.Goals.ButtonNext')}
              onClick={async () => {
                const newState = await applyNewState(props.onboardingState);
                props.onNext(newState);
              }}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default TrialWelcomeLanguage;
